import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import {
  PageContainer,
  PageHero,
  Section,
  Protected,
} from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const faqs = [
  { q: 'How do I change my role from Regular User to Contractor / Consultant / Supplier / Engineer / Workforce?', a: "Go to 'My Account' → 'Upgrade Profile', choose your desired category, fill all mandatory fields, and tap 'Upgrade Profile' to upgrade successfully." },
  { q: 'How do I add details about my product or service?', a: "Go to 'My Account' → 'My Products / Services' → 'Add Product / Service'. Provide Name, SubCategory, Images (max 3), Mobile, Email, Location, and Short & Long Description. After submission, our backend team verifies the entry. Once approved, customers can view your listing." },
  { q: "Why is there no 'Contact Now' button for my active product/service?", a: "The Contact Now button requires an active subscription plan. Choose one from 'My Account' → 'My Subscription'." },
  { q: 'How do I show or hide my Engineer / Workforce profile from the public?', a: "Toggle your profile's public visibility from 'My Account' → 'Update Details'." },
  { q: 'What happens if payment was successful but subscription not given?', a: 'If the amount is debited and the subscription is not issued, the debited amount will be refunded to your source account in 3 to 5 business days.' },
  { q: 'Is the subscription amount refundable?', a: 'Once a payment is accepted and a subscription plan is issued, there will be no refunds.' },
];

function FaqItem({ item, idx, isOpen, onToggle }) {
  return (
    <div
      className={`
        relative overflow-hidden rounded-2xl border bg-surface-paper
        transition-[border-color,background-color] duration-200 ease-out-soft
        ${isOpen ? 'border-amber/50 bg-amber/5' : 'border-border-subtle hover:border-amber/30'}
        mb-2
      `}
    >
      {isOpen && (
        <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent" />
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-${idx}-content`}
        className="flex w-full items-center gap-3 px-4 sm:px-5 py-4 text-left"
      >
        <span className={`
          inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md
          text-[10.5px] font-bold
          transition-colors duration-200
          ${isOpen ? 'bg-amber text-ink' : 'bg-amber/10 border border-amber/24 text-amber'}
        `}>
          {String(idx + 1).padStart(2, '0')}
        </span>
        <h3 className={`
          flex-1 text-[14px] sm:text-[15px] font-semibold tracking-[-0.01em] leading-[1.4]
          transition-colors duration-200
          ${isOpen ? 'text-amber' : 'text-fg-primary'}
        `}>
          {item.q}
        </h3>
        <span
          aria-hidden
          className={`
            inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full
            border transition-[transform,background-color,border-color,color] duration-200 ease-out-soft
            ${isOpen
              ? 'border-amber bg-amber text-ink rotate-180'
              : 'border-border-subtle text-fg-tertiary'}
          `}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      <div
        id={`faq-${idx}-content`}
        className={`
          grid transition-[grid-template-rows] duration-300 ease-out-soft
          ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
        `}
      >
        <div className="overflow-hidden">
          <div className="px-4 sm:px-5 pb-5 pt-1 ml-10 text-[13px] sm:text-[13.5px] leading-[1.7] text-fg-secondary">
            {item.a}
          </div>
        </div>
      </div>
    </div>
  );
}

export const FAQs = () => {
  const [metadata, setMetadata] = useState(null);
  const [openSet, setOpenSet] = useState(() => new Set([0]));

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'faqs' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({ seo_title: 'FAQs | ESAY', seo_desc: 'Frequently asked questions about ESAY.' });
    });
  }, []);

  const toggle = (idx) => () => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <Protected onCopy={(e) => e.preventDefault()}>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          {metadata.seo_desc && <meta name="description" content={metadata.seo_desc} />}
          {metadata.seo_keywords && <meta name="keywords" content={metadata.seo_keywords} />}
        </Helmet>
      )}

      <PageContainer maxWidth="md">
        <PageHero
          compact
          kicker="Support"
          title="Frequently Asked Questions"
          subtitle="Quick answers to common questions about ESAY."
        />

        <Section>
          {faqs.map((item, idx) => (
            <FaqItem
              key={idx}
              item={item}
              idx={idx}
              isOpen={openSet.has(idx)}
              onToggle={toggle(idx)}
            />
          ))}
        </Section>
      </PageContainer>
    </Protected>
  );
};
